import React from 'react';
import { Popover, Tag, Typography, Skeleton, Button, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Building2,
  Briefcase,
  UserCheck,
  Globe2,
  MapPin,
  Settings,
  CheckCircle2,
  ShieldX,
  ArrowUpRight,
} from 'lucide-react';
import { useCompanyContext } from '../../modules/settings/hooks/useCompanyContext';
import { getSeniorityLabel, getDeptLabel } from '../constants/janusTaxonomy';

const { Text } = Typography;

/**
 * IcpPopover — discreet header affordance for the tenant's Ideal Customer
 * Profile.
 *
 * Lives in the header as a subtle target icon next to the credits pill; clicking
 * it opens a compact, scannable popover: key facts as chips (sector, size,
 * country), the decision-maker role prominent with a clamped/expandable
 * description, regions and qualification criteria as wrapped chips. Capped
 * height with scroll so long ICPs never blow up the layout.
 *
 * Data source: BFF via `useCompanyContext()` — no direct backend calls.
 */
const IcpPopover: React.FC = () => {
  const navigate = useNavigate();
  const { context, isLoading } = useCompanyContext();
  const [open, setOpen] = React.useState(false);
  const [descExpanded, setDescExpanded] = React.useState(false);

  const icp = context?.icp_data;
  const setor = icp?.setor_cliente?.trim();
  const porte = icp?.porte_cliente?.trim();
  const seniorities = icp?.decisor_principal?.seniorities || [];
  const departments = icp?.decisor_principal?.departments || [];
  const decisorCargo = icp?.decisor_principal?.cargo?.trim();
  const decisorDesc = icp?.decisor_principal?.descricao?.trim();
  const seniorityLabel =
    seniorities.length > 0 ? seniorities.map((s) => getSeniorityLabel(s)).join(', ') : null;
  const deptLabel =
    departments.length > 0 ? departments.map((d) => getDeptLabel(d)).join(', ') : null;
  const pais = icp?.pais_alvo?.trim();
  const regioes = (icp?.regioes_cliente || []).filter((r) => !!r && r.trim().length > 0);
  const criterios = (icp?.criterios_qualificacao || []).filter(
    (r) => !!r && r.trim().length > 0,
  );
  const antiIcp = (icp?.anti_icp || []).filter((r) => !!r && r.trim().length > 0);

  const hasDecisor = seniorities.length > 0 || !!decisorCargo;
  const isEmpty = !setor && !porte && !hasDecisor && !pais && regioes.length === 0;

  // Reset the expand toggle whenever the popover closes so it always reopens
  // in the compact state.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setDescExpanded(false);
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: '#6366F1',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  };

  const chipStyle: React.CSSProperties = {
    margin: '0 6px 6px 0',
    background: '#EEF2FF',
    border: '1px solid #c7d2fe',
    color: '#3730A3',
    fontWeight: 500,
    borderRadius: 6,
  };

  // A labelled section block — label + free-form body.
  const section = (icon: React.ReactNode, label: string, body: React.ReactNode) => (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>
        {icon}
        {label}
      </div>
      {body}
    </div>
  );

  const clampStyle: React.CSSProperties = descExpanded
    ? { color: '#475569', fontSize: 12, lineHeight: 1.55 }
    : {
        color: '#475569',
        fontSize: 12,
        lineHeight: 1.55,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      };

  // ── Popover body ────────────────────────────────────────────────────────
  let body: React.ReactNode;

  if (isLoading) {
    body = (
      <div style={{ width: 320, padding: 4 }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  } else if (isEmpty) {
    body = (
      <div style={{ width: 300, textAlign: 'center', padding: '8px 4px' }}>
        <Target size={26} style={{ color: '#6366F1', marginBottom: 8 }} />
        <Text strong style={{ display: 'block', color: '#312E81', marginBottom: 4 }}>
          Seu Cliente Ideal
        </Text>
        <Text style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: 12, marginBottom: 14 }}>
          Ainda não foi configurado. Defina seu ICP para receber buscas, leads e
          insights mais precisos.
        </Text>
        <Button
          type="primary"
          size="small"
          icon={<Settings size={14} />}
          onClick={() => {
            setOpen(false);
            navigate('/settings/company-context');
          }}
          block
        >
          Configurar ICP
        </Button>
      </div>
    );
  } else {
    body = (
      <div style={{ width: 340 }}>
        {/* Header strip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '-16px -16px 14px',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
            borderBottom: '1px solid #c7d2fe',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Target size={16} style={{ color: '#6366F1' }} />
            <Text strong style={{ color: '#312E81', fontSize: 13 }}>
              Seu Cliente Ideal
            </Text>
          </span>
          <Tag color="blue" style={{ margin: 0, fontSize: 10, fontWeight: 600 }}>
            ICP
          </Tag>
        </div>

        {/* Scrollable content — capped so long ICPs never blow up the popover */}
        <div style={{ maxHeight: '52vh', overflowY: 'auto', paddingRight: 4 }}>
          {/* Quick facts — sector / size / country as compact key→value chips */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 12px',
              marginBottom: 14,
            }}
          >
            {[
              { icon: <Building2 size={13} />, label: 'Setor', value: setor },
              { icon: <Briefcase size={13} />, label: 'Porte', value: porte },
              { icon: <Globe2 size={13} />, label: 'País', value: pais },
            ].map((f) => (
              <div key={f.label}>
                <div style={labelStyle}>
                  {f.icon}
                  {f.label}
                </div>
                <Text style={{ fontSize: 13, color: '#1E293B', fontWeight: 500 }}>
                  {f.value || '—'}
                </Text>
              </div>
            ))}
          </div>

          <Divider style={{ margin: '0 0 14px' }} />

          {/* Decision-maker — seniority+dept labels (cargo as legacy fallback) */}
          {hasDecisor &&
            section(
              <UserCheck size={13} />,
              'Decisor',
              <div>
                {seniorityLabel && (
                  <Text style={{ fontSize: 13, color: '#1E293B', fontWeight: 600 }}>
                    {seniorityLabel}
                  </Text>
                )}
                {deptLabel && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: '#4338CA',
                      fontWeight: 500,
                      display: 'block',
                      marginTop: 2,
                    }}
                  >
                    {deptLabel}
                  </Text>
                )}
                {!seniorityLabel && decisorCargo && (
                  <Text style={{ fontSize: 13, color: '#1E293B', fontWeight: 600 }}>
                    {decisorCargo}
                  </Text>
                )}
                {decisorDesc && (
                  <>
                    <div style={{ ...clampStyle, marginTop: 4 }}>{decisorDesc}</div>
                    {decisorDesc.length > 120 && (
                      <a
                        onClick={() => setDescExpanded((v) => !v)}
                        style={{
                          fontSize: 11,
                          color: '#6366F1',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        {descExpanded ? 'ver menos' : 'ver mais'}
                      </a>
                    )}
                  </>
                )}
              </div>,
            )}

          {/* Regions */}
          {regioes.length > 0 &&
            section(
              <MapPin size={13} />,
              'Regiões',
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {regioes.map((r) => (
                  <Tag key={r} style={chipStyle}>
                    {r}
                  </Tag>
                ))}
              </div>,
            )}

          {/* Qualification criteria */}
          {criterios.length > 0 &&
            section(
              <CheckCircle2 size={13} />,
              'Critérios de qualificação',
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {criterios.map((c) => (
                  <Tag
                    key={c}
                    style={{
                      ...chipStyle,
                      background: '#ECFDF5',
                      border: '1px solid #A7F3D0',
                      color: '#065F46',
                    }}
                  >
                    {c}
                  </Tag>
                ))}
              </div>,
            )}

          {/* Anti-ICP */}
          {antiIcp.length > 0 &&
            section(
              <ShieldX size={13} />,
              'Anti-ICP',
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {antiIcp.map((a) => (
                  <Tag
                    key={a}
                    style={{
                      ...chipStyle,
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#991B1B',
                    }}
                  >
                    {a}
                  </Tag>
                ))}
              </div>,
            )}
        </div>

        <Divider style={{ margin: '6px 0' }} />
        <a
          onClick={() => {
            setOpen(false);
            navigate('/settings/company-context');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            fontSize: 12,
            color: '#6366F1',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Editar ICP
          <ArrowUpRight size={12} />
        </a>
      </div>
    );
  }

  return (
    <Popover
      content={body}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ borderRadius: 12, padding: 16, overflow: 'hidden' }}
    >
      <button
        aria-label="Seu Cliente Ideal (ICP)"
        className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
        style={{ color: isEmpty ? '#94A3B8' : '#6366F1' }}
      >
        <Target size={18} />
      </button>
    </Popover>
  );
};

export default IcpPopover;
